/**
 * Careflow Settings — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on the
 * CareflowPage. Uses the nested `getcareflows` data model with
 * transaction types: careflow_v2_*, workflow_v2_*, escalation_v2_*.
 *
 * Time values:
 *   Workflow windows: seconds from midnight (0–86400)
 *   Escalation periods: seconds from alert start (0–alert_ttl)
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';
import { NURSE_CALL_CATEGORY_FILTER } from '../../../../utils/deviceCategories.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/** Fetch all careflows (nested hierarchy) for a campus. */
export function fetchCareflows(campusId) {
    return apiFetch({
        procedure: 'getcareflows',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch area groups for a campus. */
export function fetchAreaGroups(campusId) {
    return apiFetch({
        table: 'core_area_groups',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch resident groups for a campus. */
export function fetchResidentGroups(campusId) {
    return apiFetch({
        table: 'core_resident_groups',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch user groups (mobile groups) for a campus. */
export function fetchUserGroups(campusId) {
    return apiFetch({
        procedure: 'getusersandgroups',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch all emergency contacts for a campus (resident_id = 0 returns all). */
export function fetchEmergencyContacts(campusId) {
    return apiFetch({
        procedure: 'getemergencycontacts',
        reqType: 'stored',
        payload: { resident_id: 0, campus_id: campusId },
        readOnly: true,
    });
}

/** Fetch users for a campus. */
export function fetchUsers(campusId) {
    return apiFetch({
        table: 'core_user',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch devices for a campus (Inovonics). */
export function fetchDevices(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getInovonicsDevices',
        reqType: 'stored',
        payload: {
            device_category_filter: NURSE_CALL_CATEGORY_FILTER,
            device_filter: '',
            campus_id: campusId,
        },
    });
}

/** Fetch event categories. */
export function fetchEventCategories(campusId) {
    return apiFetch({
        table: 'att_event_categories',
        procedure: 'fetchall',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch event types. */
export function fetchEventTypes(campusId) {
    return apiFetch({
        table: 'att_event_types',
        procedure: 'fetchall',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Careflow
// ---------------------------------------------------------------------------

/** Add a new careflow. */
export function addCareflow(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'careflow_v2_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit an existing careflow. */
export function editCareflow(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'careflow_v2_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

/** Delete a careflow and all its children. */
export function deleteCareflow(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'careflow_v2_delete',
        payload,
        campusId,
        userId,
        clientId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Workflow
// ---------------------------------------------------------------------------

/** Add a new workflow to a careflow. */
export function addWorkflow(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'workflow_v2_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit an existing workflow. */
export function editWorkflow(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'workflow_v2_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

/** Delete a workflow and all its children. */
export function deleteWorkflow(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'workflow_v2_delete',
        payload,
        campusId,
        userId,
        clientId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Escalation
// ---------------------------------------------------------------------------

/** Add a new escalation to a workflow. */
export function addEscalation(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'escalation_v2_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit an existing escalation. */
export function editEscalation(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'escalation_v2_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

/** Delete an escalation and all its outputs. */
export function deleteEscalation(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'escalation_v2_delete',
        payload,
        campusId,
        userId,
        clientId,
    });
}
